import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import {
  applyPostAuthRedirect,
  resolvePostAuthRedirect,
  setAuthSessionCookies,
} from "@/lib/authCookies";

// Logo image URL
const LOGO_URL = "/images/logo.png";

// Email validation helper function
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToParam = new URLSearchParams(location.search).get("returnTo");
  const from = returnToParam || location.state?.from || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setApiError("");
    
    if (touched.email) {
      if (!value) {
        setErrors((prev) => ({ ...prev, email: "Email is required" }));
      } else if (!validateEmail(value)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setApiError("");
    
    if (touched.password) {
      if (!value) {
        setErrors((prev) => ({ ...prev, password: "Password is required" }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    
    if (field === "email") {
      if (!email) {
        setErrors((prev) => ({ ...prev, email: "Email is required" }));
      } else if (!validateEmail(email)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    
    if (field === "password") {
      if (!password) {
        setErrors((prev) => ({ ...prev, password: "Password is required" }));
      } else {
        setErrors((prev) => ({ ...prev, password: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    
    // Validate
    const newErrors = {};
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!password) {
      newErrors.password = "Password is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: email,
        password: password,
      });

      const data = response.data;

      if (data.status === "error") {
        setApiError(data.message || "Login failed. Please try again.");
        setIsLoading(false);
        return;
      }

      // Login successful - save token and email in cookies
      const token = data.token;
      const accountType = data.account_type;
      setAuthSessionCookies({
        token,
        email,
        accountType,
        botType: data.bot_type,
      });

      const redirectTo = resolvePostAuthRedirect({
        accountType,
        defaultRoute: data.default_route,
        fallbackFrom: from,
        returnTo: returnToParam,
      });
      applyPostAuthRedirect(navigate, redirectTo);
    } catch (err) {
      console.error("Login error:", err);
      
      if (err.response) {
        const errorMessage = err.response.data?.detail || err.response.data?.message || "Login failed. Please try again.";
        setApiError(errorMessage);
      } else if (err.request) {
        setApiError("Network error. Please check your connection and try again.");
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
      setIsLoading(false);
    }
  };

  const isValid = email && validateEmail(email) && password;

  return (
    <div className="min-h-screen bg-[#0F1115] flex flex-col" data-testid="login-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="FractoLabs" 
              className="h-6 md:h-7 w-auto"
              data-testid="login-logo"
            />
          </Link>
          <Link 
            to="/"
            className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors font-body"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-10 radial-gradient-hero">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Card */}
          <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
                Welcome Back
              </h1>
              <p className="text-[#9CA3AF] font-body text-sm">
                Sign in to access your AI Broker dashboard
              </p>
            </div>

            {/* API Error */}
            {apiError && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="api-error">
                <p className="text-red-400 text-sm font-body text-center">{apiError}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                  <Mail className="w-4 h-4 text-[#00D1FF]" />
                  Email
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => handleBlur("email")}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                    errors.email && touched.email
                      ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                      : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                  }`}
                  data-testid="email-input"
                />
                {errors.email && touched.email && (
                  <p className="text-red-400 text-xs font-body">{errors.email}</p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                  <Lock className="w-4 h-4 text-[#00D1FF]" />
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={() => handleBlur("password")}
                    placeholder="Enter your password"
                    disabled={isLoading}
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                      errors.password && touched.password
                        ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                        : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                    }`}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && touched.password && (
                  <p className="text-red-400 text-xs font-body">{errors.password}</p>
                )}
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <Link
                  to="/password-reset"
                  className="text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isValid || isLoading}
                className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                  isValid && !isLoading
                    ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                    : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                }`}
                data-testid="login-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="my-8 flex items-center gap-4">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[#9CA3AF] text-xs font-body">OR</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Sign Up Link */}
            <p className="text-center text-[#9CA3AF] text-sm font-body">
              Don't have an account?{" "}
              <Link
                to="/"
                className="text-[#00D1FF] hover:text-[#00D1FF]/80 transition-colors font-semibold"
                data-testid="signup-link"
              >
                Get Started
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a 
              href="https://fractolabs.com/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-[#9CA3AF]/30">|</span>
            <a 
              href="https://fractolabs.com/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
            >
              Terms of Service
            </a>
          </div>
          <p className="text-[#9CA3AF]/60 text-xs font-mono">
            © 2026 FractoLabs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Login;
